import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useParams, withRouter } from 'react-router-dom';
import { useIntl } from 'react-intl';

import { getCurrentCurrency } from 'selectors';
import { usePageSchema } from 'hooks';
import { formatParsedDate, shouldNavigateToCurrencyYearArchive } from 'utils';

import Page from 'components/Page';
import PageHelmet from 'components/PageHelmet';
import LayoutContainer from 'components/LayoutContainer';
import CardsPageContent from 'components/CardsPageContent';
import PageSidebar from 'components/PageSidebar';
import PageInner from 'components/PageInner';
import HomePageFooter from 'components/HomePageFooter';

import { Content, Wrapper } from './styles';

const CardsPage = ({ location, staticContext }) => {
  const { date, currency = null } = useParams();
  const { locale, formatMessage } = useIntl();

  const currentCurrency = useSelector(state =>
    getCurrentCurrency(state, { currency })
  );

  const defaultMetaTitle = currentCurrency
    ? formatMessage(
        { id: `cardsPage.metaTitle.withCurrency` },
        {
          currency: currentCurrency?.nameG,
          isDate: date ? 1 : 0,
          date: formatParsedDate(date, 'DD MMMM YYYY', locale),
        }
      )
    : formatMessage(
        {
          id: `cardsPage.metaTitle`,
        },
        {
          isDate: date ? 1 : 0,
          date: formatParsedDate(date, 'DD MMMM YYYY', locale),
        }
      );

  const defaultMetaDescription = currentCurrency
    ? formatMessage(
        { id: `cardsPage.metaDescription.withCurrency` },
        {
          currency: currentCurrency?.nameG,
          isDate: date ? 1 : 0,
          date: formatParsedDate(date, 'DD MMMM YYYY', locale),
        }
      )
    : formatMessage(
        {
          id: `cardsPage.metaDescription`,
        },
        {
          isDate: date ? 1 : 0,
          date: formatParsedDate(date, 'DD MMMM YYYY', locale),
        }
      );

  const {
    pageScripts,
    formattedMetaTitle,
    formattedMetaDescription,
  } = usePageSchema({
    location,
    defaultMetaTitle,
    defaultMetaDescription,
  });

  if (shouldNavigateToCurrencyYearArchive(date)) {
    if (staticContext) {
      staticContext.status = 404;
    }

    return null;
  }

  return (
    <Page>
      <Wrapper>
        <LayoutContainer>
          <PageHelmet
            title={formattedMetaTitle}
            description={formattedMetaDescription}
            script={pageScripts}
          />
        </LayoutContainer>
      </Wrapper>
      <Content>
        <LayoutContainer>
          <PageInner>
            <CardsPageContent />
            <PageSidebar />
            <HomePageFooter />
          </PageInner>
        </LayoutContainer>
      </Content>
    </Page>
  );
};

CardsPage.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.objectOf(PropTypes.string).isRequired,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  staticContext: PropTypes.objectOf(PropTypes.any),
};

CardsPage.defaultProps = {
  staticContext: null,
};

export default withRouter(CardsPage);
